import classNames from 'classnames';
import { ForwardedRef, ReactNode, MouseEventHandler, CSSProperties } from 'react';
import { Link } from 'wouter';

import styles from '../../styles/buttons/basic-button.module.css';

export type BasicButtonElement = HTMLAnchorElement | HTMLButtonElement;

export interface BasicButtonProps {
    type?: 'button' | 'submit';
    href?: string | null;
    external?: boolean;
    target?: string | null;
    download?: string | null;
    disabled?: boolean;
    children?: ReactNode;
    style?: CSSProperties | null;
    clickRef?: string | null;
    className?: string | null;
    ref?: ForwardedRef<BasicButtonElement> | null;
    onClick?: MouseEventHandler<BasicButtonElement> | null;
    onPointerDown?: MouseEventHandler<BasicButtonElement> | null;
    [dataAttribute: `data-${string}`]: string | undefined;
    [ariaAttribute: `aria-${string}`]: string | boolean | undefined;
}

function BasicButton({
    type = 'button',
    href = null,
    external = false,
    target = null,
    download = null,
    children = null,
    disabled = false,
    style = null,
    clickRef = null,
    className = null,
    onClick = null,
    onPointerDown = null,
    ref: buttonRef = null,
    ...dataAttributes
}: BasicButtonProps) {
    const props = {
        ...dataAttributes,
        onClick,
        onPointerDown,
        className: classNames([styles.container, className]),
        style,
        'data-click-ref': clickRef,
    };

    if (href !== null) {
        return !external ? (
            <Link href={href} ref={buttonRef as ForwardedRef<HTMLAnchorElement>} {...props}>
                {children}
            </Link>
        ) : (
            <a
                href={href || undefined}
                target={target === null && external ? '_blank' : target}
                download={download}
                ref={buttonRef as ForwardedRef<HTMLAnchorElement>}
                {...props}
            >
                {children}
            </a>
        );
    }

    return (
        <button
            type={type}
            disabled={disabled}
            ref={buttonRef as ForwardedRef<HTMLButtonElement>}
            {...props}
        >
            {children}
        </button>
    );
}

export default BasicButton;
